import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collectionGroup,
  query,
  where,
  onSnapshot,
  type Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth-context';
import { PROJECTS, type Project } from '../types';

interface FailureRow {
  id: string;
  checklistId: string;
  description: string;
  acceptanceCriteria?: string;
  qcNotes?: string;
  screenshotUrl?: string;
  completedAt?: Timestamp;
  completedBy?: string;
  // Joined from parent checklist
  checklistTitle?: string;
  project?: Project;
}

export default function FailuresPage() {
  const { profile } = useAuth();
  const [rows, setRows] = useState<FailureRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projectFilter, setProjectFilter] = useState<Project | 'ALL'>('ALL');

  useEffect(() => {
    // collection-group query across all checklists/{id}/items where status='fail'
    const q = query(collectionGroup(db, 'items'), where('status', '==', 'fail'));
    const unsub = onSnapshot(q, {
      next: async (snap) => {
      const fetched = snap.docs.map((d) => {
        // parent path: checklists/{checklistId}/items/{itemId}
        const parent = d.ref.parent.parent;
        const checklistId = parent?.id || '';
        const data = d.data();
        return {
          id: d.id,
          checklistId,
          description: data.description,
          acceptanceCriteria: data.acceptanceCriteria,
          qcNotes: data.qcNotes,
          screenshotUrl: data.screenshotUrl,
          completedAt: data.completedAt,
          completedBy: data.completedBy
        } as FailureRow;
      });

      // Hydrate parent checklist titles + project (one read per unique checklist)
      const checklistIds = Array.from(new Set(fetched.map((r) => r.checklistId).filter(Boolean)));
      const { doc, getDoc } = await import('firebase/firestore');
      const cache: Record<string, { title?: string; project?: Project }> = {};
      await Promise.all(
        checklistIds.map(async (id) => {
          try {
            const snap = await getDoc(doc(db, 'checklists', id));
            if (snap.exists()) {
              const d = snap.data() as { title?: string; project?: Project };
              cache[id] = { title: d.title, project: d.project };
            }
          } catch {
            /* ignore */
          }
        })
      );
      for (const r of fetched) {
        const c = cache[r.checklistId];
        if (c) {
          r.checklistTitle = c.title;
          r.project = c.project;
        }
      }

      // Sort newest first
      fetched.sort((a, b) => {
        const at = a.completedAt?.toMillis?.() ?? 0;
        const bt = b.completedAt?.toMillis?.() ?? 0;
        return bt - at;
      });

      setRows(fetched);
      setError(null);
      setLoading(false);
      },
      error: (err) => {
        console.error('FailuresPage onSnapshot error:', err);
        setError(err?.message || 'Failed to load failures.');
        setLoading(false);
      }
    });
    return unsub;
  }, []);

  const filtered =
    projectFilter === 'ALL' ? rows : rows.filter((r) => r.project === projectFilter);

  const byProject = PROJECTS.map((p) => ({
    ...p,
    count: rows.filter((r) => r.project === p.value).length
  })).filter((p) => p.count > 0);

  return (
    <div className="page page--failures">
      <div className="page__header">
        <div>
          <h1 className="page__title">Failures</h1>
          <p className="page__subtitle">
            {profile?.role === 'admin'
              ? 'Every QC failure logged by Anca, across all checklists. Click any row to jump to the original item and triage.'
              : 'Items you marked as fail. Useful for catching up on what you flagged today.'}
          </p>
        </div>
      </div>

      <div className="filter-bar">
        <button
          className={`filter-bar__chip ${projectFilter === 'ALL' ? 'is-active' : ''}`}
          onClick={() => setProjectFilter('ALL')}
        >
          All ({rows.length})
        </button>
        {byProject.map((p) => (
          <button
            key={p.value}
            className={`filter-bar__chip ${projectFilter === p.value ? 'is-active' : ''}`}
            onClick={() => setProjectFilter(p.value)}
          >
            {p.label} ({p.count})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="empty">Loading…</div>
      ) : error ? (
        <div className="empty" style={{ color: '#b3261e' }}>
          ⚠️ {error}
          <div style={{ marginTop: 8, fontSize: 13, color: '#6b7280' }}>
            If this is the first time you're seeing this, a Firestore index may need to deploy. Try again in a minute.
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty">
          🎉 No failures{projectFilter === 'ALL' ? '' : ` for ${PROJECTS.find((p) => p.value === projectFilter)?.label}`}. Either everything passed or QC hasn't run yet.
        </div>
      ) : (
        <ul className="failure-list">
          {filtered.map((r) => (
            <li key={`${r.checklistId}-${r.id}`} className="failure-card">
              <Link to={`/checklists/${r.checklistId}`} className="failure-card__link">
                <div className="failure-card__head">
                  <h2 className="failure-card__title">{r.description}</h2>
                  {r.project && (
                    <span className={`tag tag--${r.project.toLowerCase()}`}>{r.project}</span>
                  )}
                </div>

                {r.checklistTitle && (
                  <div className="failure-card__parent">
                    in <span className="failure-card__parent-name">{r.checklistTitle}</span>
                  </div>
                )}

                {r.acceptanceCriteria && (
                  <div className="failure-card__criteria">
                    <strong>Expected:</strong> {r.acceptanceCriteria}
                  </div>
                )}

                {r.qcNotes && (
                  <div className="failure-card__notes">
                    <div className="failure-card__notes-label">QC notes:</div>
                    <div className="failure-card__notes-body">{r.qcNotes}</div>
                  </div>
                )}

                {r.screenshotUrl && (
                  <div className="failure-card__screenshot-link">
                    <a
                      href={r.screenshotUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      📎 Screenshot →
                    </a>
                  </div>
                )}

                {r.completedAt && (
                  <div className="failure-card__meta">
                    failed {fmtDate(r.completedAt)}
                  </div>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function fmtDate(ts: Timestamp): string {
  try {
    const d = ts.toDate();
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '';
  }
}
